import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TileEmpty } from '../TileWrapper';
import { Star, Folder } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_FAVORITES = [
  { id: '1', name: 'Site Alpha', color: '#6366f1', compliance: 92 },
  { id: '2', name: 'Main Office', color: '#22c55e', compliance: 85 },
  { id: '3', name: 'Distribution Center', color: '#ef4444', compliance: 54 },
];

export function FavoriteSpacesTile({ filter, size, useMockData }) {
  const navigate = useNavigate();
  const { company, userProfile } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_FAVORITES);
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: dashData, error } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: userProfile.id,
          p_include_archived: false,
          p_limit: 50,
          p_offset: 0,
          p_search: null,
        });

        if (error) throw error;

        // Filter to pinned projects (field is snake_case from DB)
        const pinnedIds = new Set(dashData?.settings?.pinned_projects || []);
        const favorites = (dashData?.projects || [])
          .filter(p => pinnedIds.has(p.id))
          .slice(0, 4)
          .map(p => ({
            id: p.id,
            name: p.name,
            color: p.color || '#6366f1',
            compliance: p.compliance_summary?.percentage || 0,
          }));

        setData(favorites);
      } catch (err) {
        console.error('Failed to load favorites:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, useMockData]);

  const getComplianceColor = (pct) => {
    if (pct >= 90) return 'text-green-600';
    if (pct >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getProgressColor = (pct) => {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-4">
        <div className="bg-gray-100 p-3 rounded-full mb-3">
          <Star className="h-6 w-6 text-gray-400" />
        </div>
        <p className="text-sm text-gray-600 mb-1">No favorites yet</p>
        <p className="text-xs text-gray-400">
          Pin spaces from the spaces page
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-2 pr-2">
          {data.map((space) => (
            <div
              key={space.id}
              onClick={() => navigate(`/space/${space.id}`)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: space.color + '20' }}
              >
                <Folder className="h-5 w-5" style={{ color: space.color }} />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{space.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={cn('h-full transition-all', getProgressColor(space.compliance))}
                      style={{ width: `${space.compliance}%` }}
                    />
                  </div>
                  <span className={cn('text-xs font-medium', getComplianceColor(space.compliance))}>
                    {space.compliance}%
                  </span>
                </div>
              </div>

              <Star className="h-4 w-4 text-amber-400 fill-amber-400 shrink-0" />
            </div>
          ))}
        </div>
      </ScrollArea>

      <button
        onClick={() => navigate('/spaces')}
        className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-2 border-t mt-2 shrink-0"
      >
        Manage Favorites
      </button>
    </div>
  );
}
